import API from "helpers/api";
import { getAPIResponse } from "helpers/getApiResponse";
import { Apis } from "services/api";

// Get paginated list of asset registers for a site
// parentId: undefined = all, 0 = roots only (ParentID IS NULL), N = children of N
// level: undefined = no filter, N = assets at level N (used for level-based root filtering)
const getSiteAssetRegisters = async (
	siteId,
	pageNo,
	pageSize,
	search = "",
	sortField = "",
	sortOrder = "",
	parentId,
	level,
	rootsOnly
) => {
	try {
		const sort = sortField
			? `&sortField=${sortField}&sortOrder=${sortOrder}`
			: "";
		const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
		const pagination =
			pageNo !== null ? `&pageNumber=${pageNo}&pageSize=${pageSize}` : "";
		const parentParam =
			parentId !== undefined ? `&parentId=${parentId}` : "";
		const levelParam =
			level !== undefined ? `&level=${level}` : "";
		const rootsOnlyParam =
			rootsOnly ? `&rootsOnly=true` : "";
		let response = await API.get(
			`${Apis.SiteAssetRegisters}?siteId=${siteId}${pagination}${searchParam}${sort}${parentParam}${levelParam}${rootsOnlyParam}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// Get count of asset registers for a site
// parentId: undefined = all, 0 = roots only, N = children of N
// level: undefined = no filter, N = count assets at level N
const getSiteAssetRegistersCount = async (
	siteId,
	search = "",
	parentId,
	level,
	rootsOnly
) => {
	try {
		const searchParam = search
			? `&search=${encodeURIComponent(search)}`
			: "";
		const parentParam =
			parentId !== undefined ? `&parentId=${parentId}` : "";
		const levelParam =
			level !== undefined ? `&level=${level}` : "";
		const rootsOnlyParam =
			rootsOnly ? `&rootsOnly=true` : "";
		let response = await API.get(
			`${Apis.SiteAssetRegisters}/Count?siteId=${siteId}${searchParam}${parentParam}${levelParam}${rootsOnlyParam}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// Get paginated list of asset registers for dropdown/lookup use
// Supports either siteId or siteAppId (backend derives siteId from siteAppId)
const getSiteAssetRegistersLookup = async (
	siteId,
	siteAppId,
	pageNo,
	pageSize,
	search = "",
	sortField = "",
	sortOrder = ""
) => {
	try {
		const siteParam = siteId
			? `siteId=${siteId}`
			: `siteAppId=${siteAppId}`;
		const sort = sortField
			? `&sortField=${sortField}&sortOrder=${sortOrder}`
			: "";
		const searchParam = search
			? `&search=${encodeURIComponent(search)}`
			: "";
		const pagination =
			pageNo !== null
				? `&pageNumber=${pageNo}&pageSize=${pageSize}`
				: "";
		let response = await API.get(
			`${Apis.SiteAssetRegisters}/lookup?${siteParam}${pagination}${searchParam}${sort}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// Get count of asset registers for a site (simple, no tree params)
const getSiteAssetRegistersLookupCount = async (
	siteId,
	siteAppId,
	search = ""
) => {
	try {
		const siteParam = siteId
			? `siteId=${siteId}`
			: `siteAppId=${siteAppId}`;
		const searchParam = search
			? `&search=${encodeURIComponent(search)}`
			: "";
		let response = await API.get(
			`${Apis.SiteAssetRegisters}/Count?${siteParam}${searchParam}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// Create a new asset register entry
const createSiteAssetRegister = async (data) => {
	try {
		let response = await API.post(Apis.SiteAssetRegisters, data);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// Update an asset register entry (RFC 6902 JSON Patch)
const patchSiteAssetRegister = async (id, data) => {
	try {
		let response = await API.patch(
			`${Apis.SiteAssetRegisters}/${id}`,
			data
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// Soft delete an asset register entry
const deleteSiteAssetRegister = async (id) => {
	try {
		let response = await API.delete(`${Apis.SiteAssetRegisters}/${id}`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// Upload CSV for validation/preview
const uploadSiteAssetRegisterList = async (siteId, data) => {
	try {
		let response = await API.post(
			`${Apis.SiteAssetRegisters}/${siteId}/uploadList`,
			data
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// Import CSV data
const importSiteAssetRegisters = async (siteId, data) => {
	try {
		let response = await API.post(
			`${Apis.SiteAssetRegisters}/${siteId}/import`,
			data
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// Download CSV import template
const downloadImportTemplate = async () => {
	try {
		let response = await API.get(
			`${Apis.SiteAssetRegisters}/downloadImportTemplate`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

export {
	getSiteAssetRegisters,
	getSiteAssetRegistersCount,
	getSiteAssetRegistersLookup,
	getSiteAssetRegistersLookupCount,
	createSiteAssetRegister,
	patchSiteAssetRegister,
	deleteSiteAssetRegister,
	uploadSiteAssetRegisterList,
	importSiteAssetRegisters,
	downloadImportTemplate,
};
